import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Controller,
  CreditCard,
  Envelope,
  ExclamationSquare,
  FileEarmarkCheck,
  FileEarmarkPdf,
  FileEarmarkPlus,
  FileEarmarkRuled,
  FileEarmarkText,
  House,
  People,
  PersonWorkspace,
  Receipt,
} from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetWindowSize } from '../../hooks/useGetWindowSize';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { getCurrentAccountStatementInfo } from '../../services/currentAccoutStatement';
import { useUserContext } from '../../context/UserContext';
import { IconContainer, SidenavMenuListItemDesktop, SidenavMenuListItemMobile } from './styles';

import { useStylization } from '../../hooks/useStylization';

import { bidOfferRoutePage } from '../../routes/BidOffer';
import { homeRoutePage } from '../../routes/home';
import { currentAccountStatementRoutePage } from '../../routes/CurrentAccountStatement';
import { assemblyResultsRoutePage } from '../../routes/AssemblyResults';
import { assemblyResultsResumeRoutePage } from '../../routes/AssemblyResultsResume';
import { changeAddressRoutePage } from '../../routes/ChangeAddress';
import { changePasswordRoutePage } from '../../routes/ChangePassword';
import { consolidatedPositionRoutePage } from '../../routes/ConsolidatedPosition';
import { groupsAvailabilityRoutePage } from '../../routes/GroupsAvailability';
import { quotaInformationRoutePage } from '../../routes/QuotaInformation';
import { contractRoutePage } from '../../routes/Contract';
import { billetSendRoutePage } from '../../routes/BilletSend';
import { documentsRoutePage } from '../../routes/Documents';
import { automaticDebitRoutePage, billetCreationRoutePage, membershipProposalRoutePage } from '../../routes';

const iconMap: Record<string, JSX.Element> = {
  Controller: <Controller />,
  CreditCard: <CreditCard />,
  Envelope: <Envelope />,
  ExclamationSquare: <ExclamationSquare />,
  FileEarmarkCheck: <FileEarmarkCheck />,
  FileEarmarkPdf: <FileEarmarkPdf />,
  FileEarmarkPlus: <FileEarmarkPlus />,
  FileEarmarkRuled: <FileEarmarkRuled />,
  FileEarmarkText: <FileEarmarkText />,
  House: <House />,
  People: <People />,
  PersonWorkspace: <PersonWorkspace />,
  Receipt: <Receipt />,
};

const routeMap: Record<string, string> = {
  'bidOfferRoutePage.route': bidOfferRoutePage.route,
  'homeRoutePage.route': homeRoutePage.route,
  'currentAccountStatementRoutePage.route': currentAccountStatementRoutePage.route,
  'assemblyResultsRoutePage.route': assemblyResultsRoutePage.route,
  'assemblyResultsResumeRoutePage.route': assemblyResultsResumeRoutePage.route,
  'changeAddressRoutePage.route': changeAddressRoutePage.route,
  'changePasswordRoutePage.route': changePasswordRoutePage.route,
  'billetCreationRoutePage.route': billetCreationRoutePage.route,
  'consolidatedPositionRoutePage.route': consolidatedPositionRoutePage.route,
  'groupsAvailabilityRoutePage.route': groupsAvailabilityRoutePage.route,
  'membershipProposalRoutePage.route': membershipProposalRoutePage.route,
  'quotaInformationRoutePage.route': quotaInformationRoutePage.route,
  'contractRoutePage.route': contractRoutePage.route,
  'billetSendRoutePage.route': billetSendRoutePage.route,
  'documentsRoutePage.route': documentsRoutePage.route,
  'automaticDebitRoutePage.route': automaticDebitRoutePage.route,
};

interface PageListProps {
  minimize: boolean;
  hideLabel: boolean;
  onClick?: () => void;
}

const PageList = ({ minimize, hideLabel, onClick }: PageListProps) => {
  const { group, quota, loginResponseData, digit } = useUserContext();
  const [statusCota, setStatusCota] = useState('');
  const { isMobile } = useGetWindowSize();
  const navigate = useNavigate();
  const location = useLocation();

  const { stylization } = useStylization();

  const handleClickByStatusCota = (e: React.MouseEvent<HTMLLIElement>, route: string) => {
    const linkDisabled = statusCota === 'Substituída' || statusCota === 'Cancelada';
    if (linkDisabled) {
      toast.error('Cota cancelada ou substituída');
      return;
    }
    navigate(route);
  };

  const parseIconName = (iconStr: string) => {
    const match = iconStr.match(/<(\w+)\s*\/>/);
    return match ? match[1] : '';
  };

  useEffect(() => {
    if (!group || !quota) return;

    const fetchStatus = async () => {
      try {
        const res = await getCurrentAccountStatementInfo(group, quota);
        setStatusCota(res?.statusCota ?? '');
      } catch (e) {}
    };

    fetchStatus();
  }, [group, quota, digit, loginResponseData]);

  const rawPageList = stylization?.menuNetAtend?.pageList ?? [];

  const pageList =
    Array.isArray(rawPageList) && rawPageList.length > 0
      ? (() => {
          console.log('[pageList] Usando rawPageList dinâmica');
          return rawPageList
            .map((item) => {
              const iconName = parseIconName(item.icon);
              const icon = iconMap[iconName];
              const route = routeMap[item.route] ?? item.route;

              if (!icon) {
                console.warn(`Ícone inválido para item: ${item.title}, icon recebido: ${item.icon}`);
              }

              if (!route) {
                console.warn(`Rota inválida para item: ${item.title}, route recebido: ${item.route}`);
              }

              if (!icon || !route) {
                return null;
              }

              if (item.visivel === false) {
                return null;
              }

              return { ...item, icon, route };
            })
            .filter(Boolean) as { title: string; icon: JSX.Element; route: string }[];
        })()
      : (() => {
          console.log('[pageList] Usando lista fallback estática');
          return [
            { title: 'Dashboard', icon: <People />, route: homeRoutePage.route },
            { title: 'Extrato da Conta Corrente', icon: <Receipt />, route: currentAccountStatementRoutePage.route },
            { title: 'Resultado das Assembleias', icon: <FileEarmarkText />, route: assemblyResultsRoutePage.route },
            {
              title: 'Resultado Assembleias Resumido',
              icon: <FileEarmarkPlus />,
              route: assemblyResultsResumeRoutePage.route,
            },
            { title: 'Oferta de Lance', icon: <ExclamationSquare />, route: bidOfferRoutePage.route },
            { title: 'Alteração de Endereço', icon: <House />, route: changeAddressRoutePage.route },
            { title: 'Troca de Senha', icon: <PersonWorkspace />, route: changePasswordRoutePage.route },

            { title: 'Emissão de Boleto', icon: <FileEarmarkText />, route: billetCreationRoutePage.route },
            { title: 'Posição Consolidada', icon: <Controller />, route: consolidatedPositionRoutePage.route },
            { title: 'Disponibilidade de Grupos', icon: <People />, route: groupsAvailabilityRoutePage.route },
            { title: 'Informações de Cotas', icon: <Controller />, route: quotaInformationRoutePage.route },
            { title: 'Proposta de Adesão', icon: <FileEarmarkCheck />, route: membershipProposalRoutePage.route },
            { title: 'Impressão Regulamento', icon: <FileEarmarkPdf />, route: contractRoutePage.route },
            { title: 'Envio de Boleto', icon: <Envelope />, route: billetSendRoutePage.route },
            { title: 'Documentos', icon: <FileEarmarkRuled />, route: documentsRoutePage.route },
            { title: 'Débito Automático', icon: <CreditCard />, route: automaticDebitRoutePage.route },
          ];
        })();

  if (isMobile) {
    return (
      <div>
        {pageList.map((page) => (
          <SidenavMenuListItemMobile
            key={page.title}
            active={location.pathname === page.route}
            onClick={(e) => {
              if (page.title === 'Oferta de Lance' || page.title === 'Emissão de Boleto') {
                handleClickByStatusCota(e, page.route);
                onClick?.();
                return;
              }
              navigate(page.route);
              onClick?.();
            }}
          >
            <IconContainer>{page.icon}</IconContainer>
            {page.title}
          </SidenavMenuListItemMobile>
        ))}
      </div>
    );
  }

  return (
    <>
      {pageList.map((page) => (
        <OverlayTrigger
          key={page.title}
          placement="right"
          overlay={minimize ? <Tooltip id={`tooltip-${page.title}`}>{page.title}</Tooltip> : <></>}
        >
          <SidenavMenuListItemDesktop
            active={location.pathname === page.route}
            onClick={(e) => {
              if (page.title === 'Oferta de Lance' || page.title === 'Emissão de Boleto') {
                handleClickByStatusCota(e, page.route);
                return;
              }
              navigate(page.route);
            }}
            minimize={minimize}
          >
            <IconContainer>{page.icon}</IconContainer>
            {!hideLabel && page.title}
          </SidenavMenuListItemDesktop>
        </OverlayTrigger>
      ))}
    </>
  );
};

export default PageList;
